/**
 * engine/motor.ts — núcleo de cálculo do Fincast.
 *
 * Cópia LITERAL de fincast-viabilidade-v67.html, linhas 1420-1591:
 *   calcBase (1420-1474) · calcEstatico (1476-1521) · calcFluxo (1522-1574) · calcViab (1575-1591)
 *
 * Mesmas fórmulas, mesma ordem de operações, mesmos arredondamentos, mesmos nomes de campo
 * no objeto de retorno. Onde o protótipo faz algo estranho (guarda ausente, parâmetro morto,
 * constante mock), o estranho foi PRESERVADO e comentado.
 *
 * Diferenças de assinatura (e só de assinatura):
 *   - `calcBase` recebe `premissas` como 3º parâmetro (no v67 lia a global `PREMISSAS`).
 *   - `calcFluxo` recebe `cfg` opcional com as 3 constantes MOCK, cujos defaults são
 *     exatamente os números hardcoded do protótipo. (Lote 4 de 10/10/2026, item 5: o `b`,
 *     morto na v67, passou a ser lido — o FSF unidade a unidade do Detalhamento.)
 *   - `calcViab` recebe `fmt` opcional (formatadores pt-BR), default idêntico ao v67.
 * Nenhuma função escreve em nada que veio de fora.
 */

import { teto, ymOf } from './datas';
import { demTemDados, premissasMensaisSemVendasFin, vendasDetZeradas } from './demanda';
import { estoqueDemReal, fsfPorUnidade, repasseDaUnidade, terrenoGarFrac, terrenoGarM2, tetoInformadoPJ } from './garantias';
import { FORMATADORES_PADRAO, type FormatadoresViab } from './formato';
/* lote 4 de 10/10/2026 (item 5): a soma por unidade da barra, para o fluxo mês a mês. Sem ciclo:
   igh.ts não importa daqui. */
import { _fsfTerrenoDaBarra } from './igh';
import type {
  Empreendimento, IghEstatico, Lev, LimitesDespesa, MotivoViab, PremissasMacro,
  ResultadoBase, ResultadoEstatico, ResultadoFluxo, ResultadoViab, SeriesFluxo, Ym,
} from './tipos';

/* ============================================================================
 * calcBase — motor com tabela mensal (v67 1420-1474)
 * ========================================================================== */

/**
 * @param b         empreendimento ativo (já escopado por tenant pelo repositório)
 * @param lev       cenário/alavancagem
 * @param premissas ex-global `PREMISSAS`. Só `premissas.desp` é lido, e só para
 *                  `lim`/`limErr` (faixas de despesa). Sem default de propósito: no
 *                  produto multiempresa a faixa é configuração do tenant, não do browser.
 */
export function calcBase(b: Empreendimento, lev: Lev, premissas: PremissasMacro): ResultadoBase {
  const U = b.unidades, VGV = b.VGV, Laudo = b.ctoLaudo, T = b.valorTerreno, isMCMV = (b.programa === 'MCMV');
  const d = premissas.desp;
  const lim: LimitesDespesa = {
    df: [d.dfMin / 100 * Laudo, d.dfMax / 100 * Laudo],
    dc: [d.dcMin / 100 * VGV, d.dcMax / 100 * VGV],
    ds: [0, d.segMax / 100 * Laudo],
    dl: [0, d.legMax / 100 * VGV],
  };
  /* comportamento preservado do protótipo: as 4 despesas são lidas SEM guarda `||0` */
  const df = b.despFin, dc = b.despCom, ds = b.despSeg, dl = b.despLeg;
  /* CAL-2c (12/10/2026): folga de R$ 0,50 no aviso "Extrapolou o limite" (EL-049 a EL-052),
     decisão D6 — a mesma convenção das comparações em reais do motor (hGar, hLGC, IGH). O limite
     é % × laudo/VGV: sai com resíduo binário (3,5% × 7.000.000 = 245000.00000000003) ou com
     centavos que a tela arredonda (R$ 356.172,85 aparece "R$ 356.173"), e digitar o valor
     mostrado avisava. `lim` (o que a mensagem exibe) não muda. DIVERGÊNCIA INTENCIONAL DA v67
     só na faixa de R$ 0,50; nenhum caso de tests/golden cai nela. */
  const limErr = { df: df < lim.df[0] - 0.5 || df > lim.df[1] + 0.5, dc: dc < lim.dc[0] - 0.5 || dc > lim.dc[1] + 0.5, ds: ds > lim.ds[1] + 0.5, dl: dl > lim.dl[1] + 0.5 };
  const CTE = Laudo + df + dc + ds + dl + T, CTO = isMCMV ? (CTE - df - T - dc) : (CTE - df - T);
  const preco = U ? VGV / U : 0, fracao = U ? T / U : 0;
  /* v43 item 10: mínimo de repassadas (sem EVV) = 20% do total; item 9 já usava 15%/30% em vendExig */
  const vendExig = lev.temEVV ? teto(0.15 * U) : teto(0.30 * U), finExig = lev.temEVV ? 1 : teto(0.20 * U);
  /* item 7/Q2: mínimo = maior entre input manual e a exigência de demanda */
  const salesMin = Math.max(lev.unidVendDem || 0, vendExig), finMin = Math.max(lev.unidFinDem || 0, finExig);
  const tetoPJ = Math.min(0.60 * VGV, 0.85 * CTO);
  /* EL-059 (item 7a); v49 EL-154: FSF por unidade quando a tabela de demanda tem dados.
     comportamento preservado do protótipo: no ramo agregado NÃO há `||0` (≠ calcEstatico) */
  const valorFinDem = demTemDados(lev) ? fsfPorUnidade(b, lev)
    : (lev.unidFinDem * repasseDaUnidade(preco, lev.pctFin, fracao));   /* v67 + desconto do terreno (31/07/2026) */
  /* acumulados da tabela mensal (Premissas mensais) */
  const meses = lev.meses || [];
  const acumAtIdx = (i: number) => { let ac = lev.pls0Pct || 0; for (let k = 0; k <= i && k < meses.length; k++) ac += (meses[k].obra || 0); return ac; };   /* % acum. */
  const soldCum = (i: number) => { let s = 0; for (let k = 0; k <= i && k < meses.length; k++) s += (meses[k].vendFin || 0) + (meses[k].vendNao || 0); return s; };
  const finCum = (i: number) => { let s = 0; for (let k = 0; k <= i && k < meses.length; k++) s += (meses[k].vendFin || 0); return s; };
  /* item 8: "demanda batida" = 1º mês em que unidades comercializadas e repassadas atingem os mínimos.
     Os critérios de garantias≥CTO MCV e IGH≥1,30×VGV ficam como MOCK/fora do cálculo por ora
     (decisão do usuário — Q3): não travam o mês da demanda, para não zerar o PJ (PJ = CTO − garantias). */
  let idxBatida = -1;
  for (let i = 0; i < meses.length; i++) { if (soldCum(i) >= salesMin && finCum(i) >= finMin) { idxBatida = i; break; } }
  const ymBatida: Ym | null = idxBatida >= 0 ? meses[idxBatida].ym : null;
  const demandaBatida = idxBatida >= 0;
  /* datas-limite (item 8) */
  const ymNorm: Ym | null = lev.dataContratacao ? (ymOf(lev.dataContratacao) as number) + 6 + (lev.extensaoDemanda || 0) : null;   /* item 17: prazo normativo = 6 + extensão autorizada */
  const ymDesejada: Ym | null = lev.dataDemandaDesejada ? ymOf(lev.dataDemandaDesejada) : null;   /* item 10: cumprimento desejado (soft/semáforo) */
  const dataLimiteExcede = (ymDesejada != null && ymNorm != null && ymDesejada > ymNorm);
  const ymDeadline = ymNorm;   /* prazo normativo (hard) = 6 meses após a contratação */
  /* data-limite dos cálculos (Q3): menor entre batida e o prazo */
  const ymLimite: Ym | null = (ymBatida != null && ymDeadline != null) ? Math.min(ymBatida, ymDeadline) : (ymBatida != null ? ymBatida : ymDeadline);
  /* mês-alvo da demanda: a batida; se nunca bate, último mês */
  const idxAlvo = demandaBatida ? idxBatida : (meses.length - 1);
  /* EL-062 = acum.% do mês da demanda × CTO MCV (item 7d) */
  const acumObraPct = idxAlvo >= 0 ? acumAtIdx(idxAlvo) : 0;
  const valorObra = (acumObraPct / 100) * CTO;
  const ymObra: Ym | null = idxAlvo >= 0 ? meses[idxAlvo].ym : null;
  /* EL-060 terreno como garantia — frações das financiadas na demanda até o mês-alvo (item 7c) */
  const finCumLimite = idxAlvo >= 0 ? finCum(idxAlvo) : 0;
  /* v54 item 11: Fração Ideal do EL-154; fallback m²/uniforme */
  const _tgF = terrenoGarFrac(b, lev); const _tgM2 = terrenoGarM2(b, lev);
  const valorTerrenoGar = (_tgF != null) ? _tgF : (_tgM2 != null ? _tgM2 : (lev.terrenoGarantia ? fracao * finCumLimite : 0));
  const obraAtingida = demandaBatida;   /* compat com o motivo de viabilidade */
  /* EL-061 PJ calculado (item 7) = CTO MCV − garantias, com teto no PJ máximo.
     comportamento preservado do protótipo: `lev.aporte` sem guarda `||0` (≠ calcEstatico) */
  const garantiasTotal = valorFinDem + lev.aporte + valorTerrenoGar + valorObra;
  const pjBruto = CTO - garantiasTotal;
  const pjCapped = pjBruto > tetoPJ;
  /* v45 item 11: teto do LGC só quando "PJ limitado ao LGC disponível" = Sim */
  const pjLGCmax = (lev.lgcPct > 0) ? ((b.lgcDisponivel || 0) / (lev.lgcPct / 100)) : Infinity;
  const lgcCap = lev.pjLimitadoLGC ? pjLGCmax : Infinity;
  /* EL-192 (09/10/2026): o "PJ limite" do empreendimento entra como 4º teto. Vazio = Infinity,
     e o resultado é byte a byte o de antes. `pjCapped` continua falando só do teto normativo. */
  const PJ = Math.max(0, Math.min(pjBruto, lgcCap, tetoPJ, tetoInformadoPJ(b)));
  /* antecipação em % (item 5) — limite 10% do PJ */
  const antecPct = lev.antecipPct || 0, antecExcede = antecPct > 10, antecMax = 0.10 * PJ;
  const antecip = Math.min(antecPct, 10) / 100 * PJ;
  const tomadaLGC = PJ * (lev.lgcPct / 100), saldoLGC = (b.lgcDisponivel || 0) - tomadaLGC;
  const ymDem = ymBatida;
  const demandaForaPrazo = (ymBatida == null) || (ymDeadline != null && ymBatida > ymDeadline);
  return { U, VGV, CTE, CTO, preco, fracao, lim, limErr, vendExig, finExig, salesMin, finMin, tetoPJ, valorFinDem, valorObra, acumObraPct, obraAtingida, demandaBatida, ymObra, valorTerrenoGar, finCumLimite, PJ, pjBruto, pjCapped, antecPct, antecMax, antecExcede, antecip, tomadaLGC, saldoLGC, ymDem, ymBatida, ymNorm, ymDesejada, ymDeadline, ymLimite, dataLimiteExcede, demandaForaPrazo, garantiasTotal, isMCMV };
}

/* ============================================================================
 * calcEstatico — motor SEM tabela mensal (v67 1476-1521, "v23")
 * ========================================================================== */

/**
 * Composição de garantias na data desejada, sem tabela mensal.
 * Já era 100% puro no protótipo (não lê `PREMISSAS`: não calcula lim/limErr).
 *
 * ⚠ Duplica ~15 linhas de preâmbulo de `calcBase`, mas os FALLBACKS de terreno divergem
 * DE PROPÓSITO (`calcBase` usa `finCumLimite`, vindo do fluxo; aqui usa `lev.unidFinDem`).
 * Não unificar.
 */
export function calcEstatico(b: Empreendimento, lev: Lev): ResultadoEstatico {
  const U = b.unidades, VGV = b.VGV, Laudo = b.ctoLaudo, T = b.valorTerreno, isMCMV = (b.programa === 'MCMV');
  const df = b.despFin, dc = b.despCom, ds = b.despSeg, dl = b.despLeg;
  const CTE = Laudo + df + dc + ds + dl + T, CTO = isMCMV ? (CTE - df - T - dc) : (CTE - df - T);
  const preco = U ? VGV / U : 0, fracao = U ? T / U : 0;
  const vendExig = lev.temEVV ? teto(0.15 * U) : teto(0.30 * U), finExig = lev.temEVV ? 1 : teto(0.20 * U);
  const salesMin = Math.max(lev.unidVendDem || 0, vendExig), finMin = Math.max(lev.unidFinDem || 0, finExig);
  const tetoPJ = Math.min(0.60 * VGV, 0.85 * CTO);
  /* componentes de garantia — todos calculáveis sem o fluxo temporal */
  /* FSF; v49 EL-154. O `máx(0, preço×% − fração do terreno)` é a fórmula do "Preço Médio
     de Repasse" da planilha da Caixa (MO 3041), conferida no caso VERUS — ver a nota em
     `fsfPorUnidade`. Era `preço × %` puro, e superestimava o FSF em (unidades × fração). */
  const valorFinDem = demTemDados(lev) ? fsfPorUnidade(b, lev)
    : ((lev.unidFinDem || 0) * repasseDaUnidade(preco, lev.pctFin, fracao));
  /* v54 item 11: Fração Ideal do EL-154; fallback m²/uniforme */
  const _tgF = terrenoGarFrac(b, lev); const _tgM2 = terrenoGarM2(b, lev);
  const valorTerrenoGar = (_tgF != null) ? _tgF : (_tgM2 != null ? _tgM2 : (lev.terrenoGarantia ? fracao * (lev.unidFinDem || 0) : 0));
  const valorObra = ((lev.obraGarPct || 0) / 100) * CTO;                                 /* EL-121: % informado × CTO MCV */
  const aporte = lev.aporte || 0;
  const garantiasTotal = valorFinDem + aporte + valorTerrenoGar + valorObra;
  const pjLGCmax = (lev.lgcPct > 0) ? ((b.lgcDisponivel || 0) / (lev.lgcPct / 100)) : Infinity;   /* item 4: teto de PJ pela LGC */
  const lgcCap = lev.pjLimitadoLGC ? pjLGCmax : Infinity;   /* v45 item 11 */
  /* EL-192 (09/10/2026): 4º teto, o "PJ limite" informado — ver `tetoInformadoPJ` */
  const PJ = Math.max(0, Math.min(CTO - garantiasTotal, lgcCap, tetoPJ, tetoInformadoPJ(b)));
  const pjRemanescente = PJ;                                                             /* item 4: PJ a contratar */
  const gapCTO = Math.max(0, CTO - garantiasTotal - PJ);                                 /* descoberto após garantias + PJ */
  const antecip = Math.min(lev.antecipPct || 0, 10) / 100 * PJ;
  const tomadaLGC = PJ * ((lev.lgcPct || 0) / 100), saldoLGC = (b.lgcDisponivel || 0) - tomadaLGC;
  /* datas (referência: desejada; normativo = contratação + 6m) */
  const ymDesejada: Ym | null = lev.dataDemandaDesejada ? ymOf(lev.dataDemandaDesejada) : null;
  const ymNorm: Ym | null = lev.dataContratacao ? (ymOf(lev.dataContratacao) as number) + 6 + (lev.extensaoDemanda || 0) : null;   /* item 17 */
  const dataLimiteExcede = (ymDesejada != null && ymNorm != null && ymDesejada > ymNorm);
  /* item (v34): IGH do EL-129 — SELF-CONTAINED na aba EL-093 */
  const ymDmed = ymDesejada;                                                 /* EL-065 = data de referência */
  const igh_medAc = (lev.obraGarPct || 0) / 100;                             /* EL-121 % = medição acumulada */
  const igh_GTO = Math.max(0, (1 - igh_medAc) * CTO);                        /* (1 − EL-121%) × EL-082 CTO MCV */
  const igh_SMPF = valorFinDem;                                              /* Saldo Mut. PF = FSF cheio */
  const igh_SDA = 0;                                                         /* v35: Saldo Devedor Atual = 0 no instante da demanda — evita dobrar o PJ */
  /**
   * ⚠ O DENOMINADOR DO IGH É O **PJ**, não `GTO − FSF` — corrigido em 01/08/2026.
   *
   * A planilha da Caixa (MO 3041, `D174`) escreve a regra como teto do empréstimo:
   * "Valor Máximo do Financiamento – IGH 130%" = garantia ÷ 1,30. Ou seja, o que o índice
   * protege é **o dinheiro que o banco põe**.
   *
   * O motor usava `GTO − FSF`: o custo que falta menos a parte dos compradores. Isso supõe
   * que tudo que os compradores não cobrem é EMPRESTADO — e esquece que terreno, aporte e
   * obra executada também cobrem. A conta da diferença, com `obra = o × CTO` cancelando:
   *
   *     (GTO − FSF) − PJ = terreno + aporte
   *
   * O sintoma que qualquer usuário sentiria: **aumentar o aporte não melhorava o IGH**,
   * embora reduza o empréstimo e, portanto, a exposição do banco. Na Caixa, melhora.
   *
   * ⚠ USA O PJ **DEPOIS DAS TRAVAS** (teto de LGC, teto de 60%/85% e, desde 09/10/2026, o
   * "PJ limite" do empreendimento — EL-192), por decisão de 01/08/2026: é o empréstimo real,
   * que é o que a Caixa mede. Quando a trava corta e a operação fica descoberta, quem
   * denuncia é o critério "Garantias ≥ CTO", que reprova sozinho — fazer o IGH reprovar de
   * novo pela mesma causa seria ruído, não proteção. Consequência que vale dizer na tela:
   * um "PJ limite" menor BAIXA o mínimo do IGH, mas não resolve a operação — só desloca a
   * reprovação para "Garantias ≥ CTO".
   *
   * `igh_GTO` e `igh_SMPF` continuam sendo devolvidos: a memória de cálculo (EL-184) os
   * exibe para mostrar de onde vem a necessidade de financiamento.
   */
  const igh_SMPJ = PJ;
  const ighMin = (igh_SMPJ + igh_SDA) * 1.30;                                /* Valor Mínimo em Estoque = (PJ + SD) × 1,3 */
  const precoMed = U ? VGV / U : 0;                                          /* avaliação média = EL-083 ÷ EL-084 */
  /* Parte A do IGH (29/07/2026): com a tabela EL-154 preenchida, o estoque passa a ser o
     VALOR REAL de quem ficou (+ vagas vinculadas + vagas soltas) em vez de contagem ×
     média. A RÉGUA DE SAÍDA é a mesma (EL-057 ∪ EL-114, ver `_saiuEstoqueDem`); muda o
     VALOR. Tabela vazia → `null` → contagem × média, byte a byte como antes. */
  const _estReal = estoqueDemReal(lev);
  const igh_unidEstoque = _estReal ? _estReal.unidades
    : Math.max(0, U - (lev.unidFinDem || 0) - (b.permutadas || 0));           /* EL-084 − EL-057 − EL-114 */
  const ighReal = _estReal ? _estReal.valor : igh_unidEstoque * precoMed;     /* Garantia em Estoque (realizado) */
  const ighCumpre = ighReal >= ighMin - 0.5;
  const igh: IghEstatico = { GTO: igh_GTO, SMPF: igh_SMPF, SMPJ: igh_SMPJ, SDA: igh_SDA, minEstoque: ighMin, estoque: ighReal, medAc: igh_medAc, unidEstoque: igh_unidEstoque, cumpre: ighCumpre, temUnid: !!_estReal, estatico: true };
  /* gates — duros (vermelho) e internos (amarelo); v45 fix: tolerância 0,5 no LGC */
  const hGar = (garantiasTotal + PJ) >= CTO - 0.5, hVend = (lev.unidVendDem || 0) >= salesMin, hRep = (lev.unidFinDem || 0) >= finMin, hLGC = tomadaLGC <= (b.lgcDisponivel || 0) + 0.5, hIGH = igh.cumpre;
  const hardOk = hGar && hVend && hRep && hLGC && hIGH;   /* item 16 (v26): IGH volta ao gate da tela estática */
  const sObra = (lev.obraGarPct || 0) >= (lev.minObraPct || 0);
  const softOk = sObra && !dataLimiteExcede;
  const color = !hardOk ? 'red' : (!softOk ? 'yellow' : 'green');
  return { CTE, CTO, preco, fracao, tetoPJ, vendExig, finExig, salesMin, finMin, valorFinDem, valorTerrenoGar, valorObra, aporte, garantiasTotal, PJ, pjRemanescente, gapCTO, antecip, tomadaLGC, saldoLGC, ighReal, ighMin, igh, ymDmed, ymDesejada, ymNorm, dataLimiteExcede, color, p: { hGar, hVend, hRep, hLGC, hIGH, sObra } };
}

/* ============================================================================
 * calcFluxo — fluxo mês a mês (v67 1522-1574)
 * ========================================================================== */

/**
 * As 3 constantes financeiras do fluxo.
 *
 * ⚠ SÃO MOCK: no protótipo estão hardcoded nas linhas 1551-1553 com o comentário
 * "MOCK/placeholder até vir a fórmula real" — NÃO vieram da planilha validada.
 * Promovidas a parâmetro (com estes defaults) para que o produto não carregue placeholder
 * silencioso; o comportamento default é idêntico ao do v67.
 */
export interface ConfigFluxo {
  /** juros ~0,8% a.m. sobre PJ sacado acumulado → `despFin` */
  jurosPJmes: number;
  /** amortização do PJ, 5% a.m. sobre PJ sacado acumulado → `amortPJ` */
  amortPJmes: number;
  /** rendimento ~0,4% a.m. sobre caixa acumulado → `recFin` */
  rendCaixaMes: number;
}

/** Defaults idênticos aos números hardcoded do v67 (linhas 1551-1553). */
export const CONFIG_FLUXO_PADRAO: ConfigFluxo = { jurosPJmes: 0.008, amortPJmes: 0.05, rendCaixaMes: 0.004 };

/**
 * ⚠ LOTE 4 DE 10/10/2026, ITEM 5 — o FSF e a fração do terreno UNIDADE A UNIDADE.
 *
 * Pedido (a explicação dada em 10/10: a tabela "Fluxo mensal" calcula o FSF de cada mês pela
 * média, enquanto as barras somam unidade a unidade — a barra e a tabela embaixo dela podem
 * divergir). Decisão: "Sim, neste lote (Recomendado)".
 *
 * Com o Detalhamento PREENCHIDO (`!vendasDetZeradas`), as três contas que multiplicavam uma
 * contagem de unidades pela média (repasse médio ou terreno ÷ unidades) passam a usar a MESMA
 * soma da barra do mês — `_fsfTerrenoDaBarra` (engine/igh.ts): as unidades do Detalhamento
 * financiadas, não permutadas, vendidas até o mês, pela base de cálculo (EL-191), o % próprio ou
 * o padrão e a fração ideal; as "Vendas Fin." sem unidade identificada pela média; se elas somam
 * menos, entram as de venda mais antiga. Nenhuma fórmula nova:
 *
 *   - "Acumulado Demanda" (`FSF`): a base é o FSF da barra do mês da demanda (`c.ymDem` — na tela,
 *     a barra selecionada); era `unidFinDem × repasse médio`. Logo, no mês m:
 *         FSF[m] + Σ repFSF anteriores = máx(Σ repFSF anteriores, FSF da barra × medição acumulada[m]);
 *   - "Novas Unidades Agregadas" (`repAgr`): quando as "Vendas Fin." crescem, o FSF que ENTROU na
 *     barra do mês anterior — `(barra[m−1] − barra[m−2]) × medição acumulada[m]`; era
 *     `novas × (preço − fração)`, a unidade como se fosse 100% financiada;
 *   - "Repasse Novas Vendas" (`repFra`): a fração do terreno das unidades da barra da demanda (no
 *     mês do registro) e a que entrou na barra do mês anterior; era `contagem × terreno ÷ unidades`.
 *
 * LEITURA DECLARADA: QUANTAS unidades formam a demanda é a contagem da própria barra (as "Vendas
 * Fin." acumuladas até o mês da demanda), não o campo "Unidades financiadas na demanda" — é o que
 * faz a tabela embaixo de cada barra partir do FSF daquela barra. Vale com o Detalhamento
 * preenchido (por unidade) e com ele ZERADO (a barra zerada é a contagem × a média) — revisão do
 * lote 4 (10/10/2026).
 *
 * A ÚNICA EXCEÇÃO — DECISÃO DO USUÁRIO de 11/10/2026. Pergunta (caixa), literal: "Decisão 2: nos
 * cenários sem "Vendas Fin." nas Premissas Mensais, quantas unidades formam a demanda no Fluxo
 * mensal?" Opção escolhida, literal: "O campo da demanda" — "Volta a usar o campo "Unidades
 * financiadas na demanda" só nesse caso, como antes do lote 4. A tabela e a barra podem divergir
 * quando as Premissas Mensais estão vazias." Então, quando NENHUM mês das Premissas Mensais tem
 * "Vendas Fin." (`premissasMensaisSemVendasFin`), a demanda é o campo × a média (repasse médio e
 * terreno ÷ unidades) — com o Detalhamento zerado OU preenchido; em todos os outros casos, a
 * contagem da barra. (Uma 1ª versão desta decisão, 9268450, trocou a régua pelo Detalhamento zerado:
 * mais larga — o zerado com "Vendas Fin." também ia para o campo — e incompleta — o por unidade sem
 * "Vendas Fin." o ignorava. Corrigida no mesmo dia.) Consequência aceita: a 1ª "Vendas Fin."
 * preenchida, em qualquer mês, troca a demanda do campo para a contagem da barra.
 *
 * Revisão do lote 4 (10/10/2026): "Novas Unidades Agregadas" e "Repasse Novas Vendas" contam só as
 * vendas feitas DEPOIS do mês da demanda — as de antes já estão na demanda (nos dois caminhos).
 * Mantida pela decisão de 11/10/2026 (resposta literal "Só uma vez (Recomendado)").
 *
 * As identidades estão em tests/fluxo-por-unidade.test.ts e no bloco "item 5" de
 * tests/golden/motor.test.ts.
 *
 * @param b   o empreendimento. Era PARÂMETRO MORTO (igual ao protótipo) até o lote 4 de
 *            10/10/2026: a soma por unidade precisa do terreno e do nº de unidades.
 * @param c   saída de `calcBase`.
 */
export function calcFluxo(
  b: Empreendimento,
  lev: Lev,
  c: ResultadoBase,
  cfg: ConfigFluxo = CONFIG_FLUXO_PADRAO,
): ResultadoFluxo | null {
  if (c.ymDem == null || !lev.meses.length) return null;
  const start = lev.meses[0].ym, N = Math.max(1, lev.meses.length);   /* v29 item 9: começa na 1ª data do EL-070 */
  const ymReg = c.ymDem + 1;   /* registro ~1 mês após a demanda batida */
  const baseline = (lev.pls0Pct || 0) / 100;
  /* comportamento preservado do protótipo: `c.antecip/c.CTO||0` = (antecip/CTO) || 0 —
     converte NaN/Infinity (CTO=0) em 0 */
  const limiar = c.antecip / c.CTO || 0;
  const TG = c.valorTerrenoGar;
  const obraAt = (ym: Ym) => { const m = lev.meses.find(x => x.ym === ym); return m ? (m.obra || 0) / 100 : 0; };
  const vendFinCum = (ym: Ym) => lev.meses.reduce((s, m) => s + (m.ym <= ym ? (m.vendFin || 0) : 0), 0);
  const F: SeriesFluxo = { ym: [], medEt: [], medAc: [], lib: [], unidFin: [], FSF: [], antec: [], gar: [], repFra: [], repFSF: [], repAgr: [], empPJ: [], tot: [], amortPJ: [], despFin: [], recFin: [], garAporte: [], obra: [], libGarAporte: [] };
  const aporteVal = lev.aporte || 0, ymAporte = lev.dataAporte ? ymOf(lev.dataAporte) : null;   /* item 15/16: Garantia Aporte */
  /* "Novas Unidades Agregadas" — lote 2 de 09/10/2026, item 26: com o Detalhamento ZERADO
     (`vendasDetZeradas`), a unidade nova vale pelo repasse do preço médio com o % do campo
     "% financiado do valor de venda": `repasseDaUnidade(VGV ÷ U, %, fração média)`, a mesma
     fórmula das barras e da tela de Premissas. Era `(preço − fração)` — a unidade como se
     fosse 100% financiada, a única conta do fluxo que ficara fora da "uma fórmula só" de
     31/07. Lote 4 de 10/10/2026 (item 5): com o Detalhamento preenchido, a unidade nova vale
     pela barra — ver `porMes` abaixo; a conta `(preço − fração)` saiu. */
  const zerado = vendasDetZeradas(lev);
  const repAgrUn = repasseDaUnidade(c.preco, lev.pctFin, c.fracao);
  /* Lote 4 de 10/10/2026, item 5: a demanda e as unidades novas pela MESMA soma da barra do mês
     (`_fsfTerrenoDaBarra`, com as "Vendas Fin." acumuladas até o mês — a contagem da barra; o zerado,
     pela média, desde a revisão do lote 4). Decisão do usuário de 11/10/2026 (resposta literal "O campo
     da demanda": "Volta a usar o campo "Unidades financiadas na demanda" só nesse caso, como antes do
     lote 4"): SEM NENHUMA "Vendas Fin." nas Premissas Mensais — e só aí, com o Detalhamento zerado ou
     preenchido —, o campo × a média, as expressões de antes do lote 4, byte a byte. */
  const dem = premissasMensaisSemVendasFin(lev) ? null : _fsfTerrenoDaBarra(b, lev, c, c.ymDem, vendFinCum(c.ymDem));
  const fsfDem = dem ? dem.fsf : lev.unidFinDem * repasseDaUnidade(c.preco, lev.pctFin, c.fracao);
  const terDem = dem ? dem.terreno : lev.unidFinDem * c.fracao;
  const porMes: Array<{ fsf: number; terreno: number }> = [];   /* a barra de cada mês do fluxo (só com o Detalhamento preenchido) */
  let garAcum = 0, repFSFAcum = 0, cumPJ = 0, cumCaixa = 0, aporteBloq = aporteVal;   /* item 14: aporte bloqueado como garantia */
  for (let m = 0; m < N; m++) {
    const ym = start + m; F.ym.push(ym); const et = obraAt(ym); F.medEt.push(et);
    const prevAc = m ? F.medAc[m - 1] : baseline; const ac = prevAc + et; F.medAc.push(ac);
    const lib = ac <= limiar ? 0 : (prevAc <= limiar ? (ac - limiar) * c.CTO : et * c.CTO); F.lib.push(lib);
    const uf = vendFinCum(ym); F.unidFin.push(uf);
    if (!zerado) porMes.push(_fsfTerrenoDaBarra(b, lev, c, ym, uf));
    /* 31/07/2026: passa pelo `repasseDaUnidade`, a MESMA fórmula do estático e das barras.
       Antes era `(preço − fração)`: descontava o terreno mas ignorava o percentual
       financiado, então superestimava a entrada de caixa sempre que `pctFin < 100%`.
       Lote 4 (item 5): `fsfDem` — sem nenhuma "Vendas Fin." (decisão de 11/10/2026), `unidFinDem × repasse
       médio` (a mesma ordem de antes). */
    const fsf = m === 0 ? 0 : Math.max(0, fsfDem * ac - repFSFAcum); F.FSF.push(fsf);
    const at = (ym === c.ymDem) ? c.antecip : 0; F.antec.push(at);
    let g = 0; if (TG && m > 0) { g = Math.min(lib, TG - garAcum); garAcum += g; } F.gar.push(g);
    /* revisão do lote 4 (10/10/2026): só é "nova" a venda feita DEPOIS do mês da demanda. A de antes
       já está na demanda — o FSF no "Acumulado Demanda", o terreno no registro (ou em garantia) — e
       entrava de novo em "Novas Unidades Agregadas" e "Repasse Novas Vendas" (herança da v67, que
       fazia o mesmo com o campo × a média: Medição + Novas passava do FSF de TODAS as financiadas). */
    const dPrev = (m >= 2 && F.ym[m - 1] > c.ymDem) ? (F.unidFin[m - 1] - F.unidFin[m - 2]) : 0;
    /* lote 4 (item 5): com o Detalhamento preenchido, a fração do terreno que entrou na barra do mês anterior */
    const novasTer = zerado ? dPrev * c.fracao : (dPrev !== 0 ? porMes[m - 1].terreno - porMes[m - 2].terreno : 0);
    const rf = (ym === ymReg ? Math.max(0, terDem - TG) : 0) + novasTer; F.repFra.push(rf);
    const base = m === 0 ? 0 : ((F.unidFin[m] === F.unidFin[m - 1] && lib <= fsf) ? lib : fsf);
    const rfsf = Math.max(0, base - g); F.repFSF.push(rfsf); repFSFAcum += rfsf;
    /* item 26: ver `repAgrUn` · lote 4 (item 5): com o Detalhamento preenchido, o FSF que entrou na barra do mês anterior */
    const ragr = dPrev > 0 ? (zerado ? dPrev * repAgrUn : porMes[m - 1].fsf - porMes[m - 2].fsf) * ac : 0; F.repAgr.push(ragr);
    const epj = Math.max(0, lib - g - rfsf); F.empPJ.push(epj);
    F.tot.push(at + g + rf + rfsf + ragr + epj);
    /* v29 item 10 — linhas financeiras (MOCK/placeholder até vir a fórmula real) */
    cumPJ += epj;
    F.despFin.push(cfg.jurosPJmes * cumPJ);                 /* v67: 0.008*cumPJ */
    F.amortPJ.push(cfg.amortPJmes * cumPJ);                 /* v67: 0.05*cumPJ */
    F.recFin.push(cfg.rendCaixaMes * Math.max(0, cumCaixa)); /* v67: 0.004*max(0,cumCaixa) */
    cumCaixa += F.tot[m];
    F.garAporte.push((aporteVal > 0 && ymAporte != null && ym === ymAporte) ? aporteVal : 0);   /* saída do aporte na Data do Aporte */
    /* v33 item 2: Obra = % Custo do mês × "Custo real total da obra" — saída */
    const custoPct = ((lev.meses[m] && lev.meses[m].obraCusto) || 0) / 100;
    F.obra.push(custoPct * (lev.custoRealObra || 0));
    /* item 14: Liberação Garantia Aporte — reentrada conforme a composição supera o CTO MCV */
    let libAp = 0;
    if (aporteVal > 0 && ymAporte != null && ym >= ymAporte) {
      const garComp = fsf + garAcum + ac * c.CTO;                            /* FSF + terreno acum. + obra executada */
      const alvoBloq = Math.min(aporteVal, Math.max(0, c.CTO - garComp));    /* aporte ainda necessário p/ cobrir o CTO */
      if (alvoBloq < aporteBloq) { libAp = aporteBloq - alvoBloq; aporteBloq = alvoBloq; }
    }
    F.libGarAporte.push(libAp);
  }
  const primeiraLib = F.ym.findIndex((_, i) => F.repFSF[i] + F.repAgr[i] + F.repFra[i] + F.empPJ[i] > 0);
  /* comportamento preservado do protótipo: `lev.aporte` sem guarda `||0` */
  const caixa12 = F.tot.slice(0, 12).reduce((a, v) => a + v, 0) - lev.aporte;
  const totalGeral = F.tot.reduce((a, v) => a + v, 0);
  const pjLiberado = F.empPJ.reduce((a, v) => a + v, 0) + c.antecip;
  /* item 8: TOTAL líquido = entradas − saídas (inclui Obra / Liberação Garantia Aporte) */
  const net = F.ym.map((_, m) => F.FSF[m] + F.gar[m] + F.antec[m] + F.repFra[m] + F.repFSF[m] + F.repAgr[m] + F.empPJ[m] + F.recFin[m] + F.libGarAporte[m] - (F.obra[m] + F.amortPJ[m] + F.garAporte[m] + F.despFin[m]));
  const netGeral = net.reduce((a, v) => a + v, 0);
  /* o spread reproduz a ORDEM das chaves do objeto `F` do protótipo:
     as 19 séries, depois primeiraLib, caixa12, totalGeral, pjLiberado, net, netGeral */
  return { ...F, primeiraLib, caixa12, totalGeral, pjLiberado, net, netGeral };
}

/* ============================================================================
 * calcViab — os 5 motivos de viabilidade (v67 1575-1591)
 * ========================================================================== */

/**
 * ⚠ MISTURA REGRA E APRESENTAÇÃO — e assim foi extraída, porque nesta fatia o motor não
 * pode mudar de comportamento. `motivos[].ok` e `viavel` são puros; `motivos[].m` e
 * `.kpi` são frases pt-BR formatadas. Quem só precisa da regra deve ler `ok`/`viavel` e
 * ignorar as strings.
 *
 * @param _F  PARÂMETRO MORTO — nunca usado no corpo (igual ao protótipo); os 3 chamadores
 *            do v67 passam mesmo assim. Mantido na posição por compatibilidade.
 * @param fmt formatadores injetáveis (default = os do protótipo, dependentes do ICU).
 */
export function calcViab(
  b: Empreendimento,
  lev: Lev,
  c: ResultadoBase,
  _F?: ResultadoFluxo | null,
  fmt: FormatadoresViab = FORMATADORES_PADRAO,
): ResultadoViab {
  const { moeda0, num, ymLabel } = fmt;
  const cut = c.ymLimite;
  const obraFisica = (ym: Ym) => ((lev.pls0Pct || 0) + lev.meses.reduce((s, m) => s + (m.ym <= ym ? (m.obra || 0) : 0), 0));
  const vendasCum = (ym: Ym) => lev.meses.reduce((s, m) => s + (m.ym <= ym ? ((m.vendFin || 0) + (m.vendNao || 0)) : 0), 0);
  const finCum = (ym: Ym) => lev.meses.reduce((s, m) => s + (m.ym <= ym ? (m.vendFin || 0) : 0), 0);
  const oF = cut != null ? obraFisica(cut) : 0, vC = cut != null ? vendasCum(cut) : 0, fC = cut != null ? finCum(cut) : 0;
  /* motivos carregam kpi curto com atingido/mín; item 6: unidades comercializadas explícitas.
     A ORDEM dos 5 motivos faz parte do congelamento — não reordenar. */
  const motivos: MotivoViab[] = [
    { t: 'Demanda batida dentro do prazo', ok: c.demandaBatida && !c.demandaForaPrazo, m: c.demandaBatida ? `Demanda batida em ${c.ymBatida != null ? ymLabel(c.ymBatida) : '—'}${c.demandaForaPrazo ? ` · ⚠ após o limite ${c.ymDeadline != null ? ymLabel(c.ymDeadline) : ''}` : ` · garantias ${moeda0(c.garantiasTotal)}`}` : 'Unidades insuficientes na tabela para bater a demanda', kpi: c.demandaBatida ? `Batida ${c.ymBatida != null ? ymLabel(c.ymBatida) : ''}` : 'Não batida' },
    /* CAL-2b (12/10/2026): folga de resíduo binário da soma da obra (1e-9 ponto percentual,
       decisão D5) — 2,9 + 7,3 + 4,1 + 0,7 dá 14.999999999999998 e reprovava "15,0% vs mínimo
       15%". Só o resíduo: 14,96% continua reprovado. SEM `|| 0` de propósito, para manter a
       semântica da v67 (mínimo null aprova; undefined → NaN → reprova). DIVERGÊNCIA INTENCIONAL
       DA v67 só nessa faixa; nenhum caso de tests/golden cai nela. */
    { t: 'Mínimo de obra executada até a demanda', ok: oF >= lev.minObraPct - 1e-9, m: `Executado ${oF.toFixed(1).replace('.', ',')}% vs mínimo ${lev.minObraPct}%`, kpi: `Obra ${oF.toFixed(1).replace('.', ',')}% (mín ${lev.minObraPct}%)` },
    /* CAL-2 (12/10/2026): mesma folga de R$ 0,50 do hLGC (calcEstatico) e da barra (igh.ts,
       serieGarantias) — o teto do PJ divide pela taxa e a tomada multiplica de volta; o resíduo
       binário (1000000.0000000001) reprovava. DIVERGÊNCIA INTENCIONAL DA v67, que comparava sem
       folga: nenhum caso de tests/golden cai na faixa (0; 0,5]. `|| 0` como no hLGC. Prova em
       tests/folga-arredondamento.test.ts. */
    { t: 'LGC disponível × tomado', ok: c.tomadaLGC <= (b.lgcDisponivel || 0) + 0.5, m: `Tomada ${moeda0(c.tomadaLGC)} vs disponível ${moeda0(b.lgcDisponivel)}`, kpi: `LGC ${moeda0(c.tomadaLGC)} (disp ${moeda0(b.lgcDisponivel)})` },
    { t: 'Unidades comercializadas até a data limite', ok: vC >= c.salesMin, m: `Comercializadas ${num(vC)} vs exigidas ${num(c.salesMin)}${c.demandaForaPrazo ? ' · ⚠ fora do prazo' : ''}`, kpi: `Comercializadas ${num(vC)} (mín ${num(c.salesMin)})` },
    { t: 'Unidades repassadas até a data limite', ok: fC >= c.finMin, m: `Repassadas ${num(fC)} un. vs exigidas ${num(c.finMin)}`, kpi: `Repassadas ${num(fC)} (mín ${num(c.finMin)})` },
  ];
  const viavel = motivos.every(x => x.ok) && !c.demandaForaPrazo;
  return { motivos, viavel, oF, vC, fC };
}
